/**
 * s232 (chapter two, PR B) — where a chapter-two cell's answer lives, read from the ONE hand-kept map.
 *
 * Every chapter-two cell is named `<tile id>.<k>` (scripts/lib/board-parity.ts `optionCells`). Its field keys are
 * `CELL_FIELD[screen][id]` and its stored words are `OPTION_VALUE[screen][id]` (shared/interview/board-map.ts) — the
 * same entries `check:board-parity` holds against the board. The screens keep NO copy of either: a new cell on the
 * board fails the check until it is mapped, and once mapped the screen reads it from here. Pure.
 */
import { BRANCH_ALSO_OPENS, CELL_FIELD, DRAWN_CELLS, OPTION_VALUE, PIN_CELL_FIELD } from "@shared/interview/board-map";
import type { Ch2Cell } from "@shared/interview/board-ch2";

export const cellId = (tile: string, k: string | undefined): string => `${tile}.${k ?? ""}`;

/** The field keys a cell writes, in the map's order (a person cell: its name, then phone and email). */
export function fieldsOf(screen: string, tile: string, cell: Pick<Ch2Cell, "k">): readonly string[] {
  // s233 (D-233.81): a pinned home row's own lines are mapped in PIN_CELL_FIELD (the parity census reads table cells only).
  return CELL_FIELD[screen]?.[cellId(tile, cell.k)] ?? PIN_CELL_FIELD[screen]?.[cellId(tile, cell.k)] ?? [];
}

/** The ONE field a cell writes (its first). Empty when the cell is drawn, not built. */
export function fieldOf(screen: string, tile: string, cell: Pick<Ch2Cell, "k">): string {
  return fieldsOf(screen, tile, cell)[0] ?? "";
}

/** Drawn on the board, not built yet (board-map.ts DRAWN_CELLS) — the screen leaves it out. */
export function isDrawn(screen: string, tile: string, cell: Pick<Ch2Cell, "k">): boolean {
  return (DRAWN_CELLS[screen] ?? []).includes(cellId(tile, cell.k));
}

/** A board word → the value stored for it (identity when the map lists none). */
export function storedOf(screen: string, tile: string, cell: Pick<Ch2Cell, "k">, word: string): string {
  return OPTION_VALUE[screen]?.[cellId(tile, cell.k)]?.[word] ?? word;
}

/** A stored value → the board word it was stored for; "" when it matches none (an answer the board does not offer). */
/**
 * s234 walk (item B): the answer whose `then` branch is open — the stored answer's word, or the branch the board map opens
 * on another of the row's answers (BRANCH_ALSO_OPENS: an Investment Property opens Rented out?'s Yes branch).
 */
export function branchWordOf(screen: string, tile: string, cell: Pick<Ch2Cell, "k" | "opts">, values: Readonly<Record<string, string>>): string {
  const word = wordOf(screen, tile, cell, values[fieldOf(screen, tile, cell)] ?? "");
  if (word) return word;
  const also = BRANCH_ALSO_OPENS[screen]?.[cellId(tile, cell.k)];
  return also && (values[also.field] ?? "") === also.equals ? also.branch : "";
}

export function wordOf(screen: string, tile: string, cell: Pick<Ch2Cell, "k" | "opts">, stored: string): string {
  if (!stored) return "";
  const map = OPTION_VALUE[screen]?.[cellId(tile, cell.k)];
  if (!map) return (cell.opts ?? []).includes(stored) ? stored : "";
  return Object.entries(map).find(([, v]) => v === stored)?.[0] ?? "";
}

/** The picker / segment options for a cell: the board's words as labels, the stored values as values. */
export function optionsOf(screen: string, tile: string, cell: Pick<Ch2Cell, "k" | "opts">): { value: string; label: string }[] {
  return (cell.opts ?? []).map((w) => ({ value: storedOf(screen, tile, cell, w), label: w }));
}

/** The board's typed-Other word — a picker offering it opens the type-it box under its column (rule 41). s237: declared
 *  once, with the shared box (../OtherBox). */
export { OTHER_WORD } from "../OtherBox";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A typed section's REAL row: its id is its record's (a seeded `__seeded__…` scaffold is never a record). */
export const isRecordRow = (rowId: string): boolean => UUID.test(rowId);

/**
 * `offWhen` / `unless` (the s225 build requirement): a line switched off by another answer on the same row — "Left to
 * someone?" is off when the place is held jointly, unless the planner said the joint owner was added only to help.
 */
export function isOff(screen: string, tile: string, line: Ch2Cell, lines: readonly Ch2Cell[], values: Readonly<Record<string, string>>): boolean {
  const w = line.offWhen;
  if (!w) return false;
  const find = (k: string): Ch2Cell | undefined => {
    const walk = (ls: readonly Ch2Cell[] | undefined): Ch2Cell | undefined => {
      for (const l of ls ?? []) {
        if (l.k === k) return l;
        const inner = Array.isArray(l.then) ? walk(l.then) : l.then ? Object.values(l.then).map(walk).find(Boolean) : undefined;
        if (inner) return inner;
      }
      return undefined;
    };
    return walk(lines);
  };
  const answer = (k: string): string => {
    const c = find(k);
    return c ? wordOf(screen, tile, c, values[fieldOf(screen, tile, c)] ?? "") : "";
  };
  if (answer(w.k) !== w.v) return false;
  if (w.unless && w.unless.v.includes(answer(w.unless.k))) return false;
  return true;
}

/** s233 (D-233.34): `moneyDraft` retired — a money box is `IconTextCell money` (InterviewCells), which reads through #153's formatter. */
